import React, { Suspense } from 'react'
import { Layout } from 'antd'
import { Router, Route, Redirect, Switch } from 'react-router-dom'
import { Provider } from 'react-redux'
import { v4 as uuid } from 'uuid'

import history from './history'
import logger from './services/errorlogger'
import initiateCirsp from './services/crisp'

import createStore from './store/createStore'

import AuthenticationMonitor from './auth/AuthMonitor'
import PrivateRoute from './auth/PrivateRoute.jsx'

import UpgradeModal from './components/UpgradeModal/UpgradeModal'
import Header from './components/Header/Header'
import Footer from './components/Footer/Footer'
import Loading from 'components/Loading/Loading'

import './App.css'

const DashboardPage = React.lazy(() => import('./pages/Dashboard/DashboardPage'))
const SiteManagerPage = React.lazy(() => import('./pages/SiteManager/SiteManagerPage'))
const SiteReportFullPage = React.lazy(() => import('./pages/SiteReport/SiteReportFullPage'))
const AccountPage = React.lazy(() => import('./pages/Account/AccountPage'))
const NotificationsPage = React.lazy(() => import('./pages/Notifications/NotificationsPage'))
const AdminPage = React.lazy(() => import('./pages/Admin/AdminPage'))
const LoginPage = React.lazy(() => import('./pages/Auth/LoginPage'))
const LogoutPage = React.lazy(() => import('./pages/Auth/LogoutPage'))
const SignupPage = React.lazy(() => import('./pages/Auth/SignupPage'))
const VerifyEmailPage = React.lazy(() => import('./pages/Auth/VerifyEmailPage'))
const AccountInactivePage = React.lazy(() => import('./pages/Auth/AccountInactivePage'))
const PricingPage = React.lazy(() => import('./pages/Pricing/PricingPage'))
const CheckoutPage = React.lazy(() => import('./pages/Checkout/CheckoutPage'))
const PaymentConfirmationPage = React.lazy(() => import('./pages/Checkout/PaymentConfirmationPage'))
const PaymentSuccessPage = React.lazy(() => import('./pages/Checkout/PaymentSuccessPage'))
const OnboardingPage = React.lazy(() => import('./pages/Onboarding/OnboardingPage'))

const store = createStore()

const isProduction = process.env.NODE_ENV === 'production'

logger.init()

const App = () => {
  if (isProduction) initiateCirsp()
  if (localStorage && !localStorage.getItem('sessionId')) localStorage.setItem('sessionId', uuid())

  return (
    <Provider store={store}>
      <div className="App">
        <Router history={history}>
          <AuthenticationMonitor>
            <Layout>
              <Header />
              <Layout>
                <Layout.Content className="app-content">
                  <Suspense fallback={<Loading />}>
                    <Switch>
                      <Route
                        path="/"
                        exact
                        component={(props) => (
                          <Redirect to={{ pathname: '/login', state: { from: props.location } }} />
                        )}
                      />
                      <Route path="/login/" component={LoginPage} />
                      <Route path="/logout/" component={LogoutPage} />
                      <Route exact path="/sign-up/" component={SignupPage} />
                      <Route exact path="/verify/" component={VerifyEmailPage} />
                      <Route
                        exact
                        path="/verify/:userId/:emailVerifyId"
                        component={VerifyEmailPage}
                      />
                      <Route path="/inactive/" component={AccountInactivePage} />

                      {/*
                        TRIAL FLOW IS DISABLED (2026-10-11). /start and /start/:siteUrl now redirect to /login.

                        What /start was: a public "check your site for free" page (SiteReportDemoPage).
                        It POSTed to /api/jobs/trial/run, which ran the job through the manager's separate
                        JobManagerTrial flow (manager/app/JobManager/JobManagerTrial.js), no account needed.

                        Why it is off:
                        - Nobody uses it. The api logs from about 2026-09-04 to 2026-10-11 and 14 days of
                          nginx logs show no real trial run, only bots (WebSiphon) hitting the URL. The
                          newest log with real trial runs dates from May 2021. Firebase has one trial job
                          left (thankyourskin.com, 2020); CleanJobsScheduler deletes trial jobs every month.
                        - It is broken. JobManagerTrial saves products only to Firebase `products/{jobId}`,
                          but this page loads them through GET /api/products/:jobId, which reads Mongo. A
                          trial run would show an empty report. The isExample link in NotificationModal
                          (thankyourskin.com) has the same problem.
                        - It is an open door. /api/jobs/trial/run has no auth, captcha or rate limit and
                          starts a real crawl, which costs proxy and Keepa usage, for anyone who calls it.

                        To bring it back, the trial flow must write products to Mongo (as JobManager does
                        through appendProductsDB) and the endpoint needs abuse protection. Re-enabling
                        these routes is not enough. The API route is disabled too, see
                        app/controllers/router.js.
                      */}
                      <Route
                        path="/start/"
                        component={(props) => (
                          <Redirect to={{ pathname: '/login', state: { from: props.location } }} />
                        )}
                      />

                      <Route exact path="/app/pricing/" component={PricingPage} />
                      <Route exact path="/app/checkout/" component={CheckoutPage} />
                      <Route
                        exact
                        path="/app/payment/confirmation/"
                        component={PaymentConfirmationPage}
                      />
                      <Route exact path="/app/payment/success/" component={PaymentSuccessPage} />

                      <PrivateRoute exact path="/app/dashboard/" component={DashboardPage} />
                      <PrivateRoute exact path="/app/onboarding/" component={OnboardingPage} />
                      <PrivateRoute exact path="/app/site/:siteId" component={SiteManagerPage} />
                      <PrivateRoute
                        path="/app/report/:siteId/:jobId"
                        component={SiteReportFullPage}
                      />
                      <PrivateRoute path="/app/account/" component={AccountPage} />
                      <PrivateRoute
                        exact
                        path="/app/notifications/"
                        component={NotificationsPage}
                      />

                      <PrivateRoute exact path="/app/admin/" component={AdminPage} />

                      <Redirect to="/app/dashboard/" />
                    </Switch>
                  </Suspense>
                </Layout.Content>
              </Layout>
              <Footer />
              <UpgradeModal />
            </Layout>
          </AuthenticationMonitor>
        </Router>
      </div>
    </Provider>
  )
}

export default App
